import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { type CSSProperties, forwardRef } from 'react';
import { type AllowedButtonTags, Button, headlessButtonProps } from '../Button/server';
import { Icon, type IconProps } from '../Icon';
import type { Sizing } from '../types';
import styles from './IconButton.module.css';
import { type IconButtonProps, type IconButtonType } from './types';
const iconSizes = {
  small: '200',
  default: '300',
  input: '300',
  large: '400'
} as const;
function IconSlot({
  enabled,
  ...props
}: Omit<IconProps, 'size'> & {
  enabled?: boolean;
  size: Sizing;
}) {
  const cssVar = `var(--betterplace-sizing-${props.size})`;
  return <span className={classNames(styles.iconSlot, {
    [styles.hasIcon!]: enabled
  })} style={{
    '--icon-size': cssVar,
    'width': cssVar
  } as CSSProperties}>
      {enabled && <Icon {...props} />}
    </span>;
}
function headlessIconButtonPropsInternal<T extends AllowedButtonTags>({
  children: children_,
  size = 'default',
  className: userClassName,
  iconName,
  iconPosition = 'left',
  iconProps = {},
  ...props
}: IconButtonProps<T>) {
  const className = classNames(styles.iconButton, {
    [userClassName as string]: userClassName,
    [styles.iconOnly as string]: !children_ && !!iconName
  });
  const hasIcon = !!iconName;
  const hasIconRight = hasIcon && iconPosition === 'right';
  const hasIconLeft = hasIcon && iconPosition === 'left';
  const iconSize = iconProps.size ?? iconSizes[size];
  const children = <>
      <IconSlot {...iconProps} name={iconName!} size={iconSize} enabled={hasIconLeft} />
      {children_}
      <IconSlot {...iconProps} name={iconName!} size={iconSize} enabled={hasIconRight} />
    </>;
  return {
    ...props,
    children,
    className,
    size
  };
}
export function headlessIconButtonProps<T extends AllowedButtonTags>(props: IconButtonProps<T>) {
  const buttonProps = headlessIconButtonPropsInternal<T>(props);
  const props_ = headlessButtonProps<T>(buttonProps);
  return props_;
}
const IconButton = forwardRef<HTMLButtonElement, IconButtonProps<'button'>>(function IconButton(props, ref) {
  const $ = _c(9);
  let t0;
  if ($[0] !== props) {
    t0 = headlessIconButtonPropsInternal(props);
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let children;
  let props_;
  if ($[2] !== t0) {
    ({
      children,
      ...props_
    } = t0);
    $[2] = t0;
    $[3] = children;
    $[4] = props_;
  } else {
    children = $[3];
    props_ = $[4];
  }
  let t1;
  if ($[5] !== children || $[6] !== props_ || $[7] !== ref) {
    t1 = <Button ref={ref} {...props_}>{children}</Button>;
    $[5] = children;
    $[6] = props_;
    $[7] = ref;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
}) as IconButtonType;
export default IconButton;